import { useCallback, useEffect, useState } from 'react';

interface IFullscreenHook {
  isFullscreen: boolean;
  toggleFullscreen: () => void;
}

export default function useFullscreen(
  containerRef: React.RefObject<HTMLElement | null>,
  videoRef: React.RefObject<HTMLVideoElement | null>
): IFullscreenHook {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleChange = () => {
      const entering = !!document.fullscreenElement;
      setIsFullscreen(entering);

      // Clear inline styles set by useCheckForResize so CSS width/height: 100% applies
      const video = videoRef.current;
      if (video && entering) {
        video.style.width = '';
        video.style.height = '';
      }
    };

    document.addEventListener('fullscreenchange', handleChange);
    document.addEventListener('webkitfullscreenchange', handleChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleChange);
      document.removeEventListener('webkitfullscreenchange', handleChange);
    };
  }, [videoRef]);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    const video = videoRef.current;

    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
      return;
    }

    // Standard Fullscreen API on container (keeps custom controls visible)
    if (container?.requestFullscreen) {
      container.requestFullscreen().catch(() => {});
      return;
    }

    // Webkit fallback (iOS Capacitor) — fullscreens the video element directly
    const videoEl = video as HTMLVideoElement & {
      webkitEnterFullscreen?: () => void;
    };
    if (videoEl?.webkitEnterFullscreen) {
      videoEl.webkitEnterFullscreen();
    }
  }, [containerRef, videoRef]);

  return { isFullscreen, toggleFullscreen };
}
